/**
 * The router's `errorElement`: whatever a page throws while rendering, loading or acting lands here
 * instead of React Router's own unstyled crash screen. Reloading often clears it (a stale chunk after a
 * deploy, a one-off render bug); "Go to dashboard" gets the customer somewhere useful when it doesn't.
 */
import { useEffect, useMemo } from "react";
import { Link, useRouteError } from "react-router";

import { Button, buttonClass } from "@/components/ui";

/** A short id to quote to support, distinct enough to find this one crash in the logs. */
function newReferenceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID().slice(0, 8);
  return Math.random().toString(36).slice(2, 10);
}

/** A new build's assets replaced the ones this tab's old `index.html` still asks for. */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Loading chunk/i.test(message);
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const referenceId = useMemo(() => newReferenceId(), []);
  const staleChunk = isStaleChunkError(error);

  useEffect(() => {
    console.error(`[crash ${referenceId}]`, error);
  }, [error, referenceId]);

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-3 px-6 py-16 text-center">
      <h1 className="text-lg font-semibold text-fg">
        {staleChunk ? "A new version of CortexGrip is available" : "Something went wrong"}
      </h1>
      <p className="max-w-sm text-sm text-fg-muted">
        {staleChunk
          ? "Reload the page to pick up the latest version."
          : "This page ran into a problem and couldn't continue. Reloading usually fixes it."}
      </p>
      <div className="mt-2 flex gap-2">
        <Button
          unstyled
          type="button"
          className={buttonClass("primary")}
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
        {!staleChunk && (
          <Link to="/" className={buttonClass("secondary")}>
            Go to dashboard
          </Link>
        )}
      </div>
      <p className="mt-2 text-xs text-fg-subtle">
        Reference: <span className="font-mono">{referenceId}</span>
      </p>
    </div>
  );
}
